// Generator dokumen "Daftar Hadir Evaluasi Tender" untuk tab Attendance
// Confirmation di Tender Evaluation.
//
// Dibangun murni client-side sebagai HTML (tanpa library PDF), mengikuti pola
// `Crude/.../tenderInvitationDoc.js`:
//  - printAttendancePdf -> buka dokumen di tab baru lalu panggil
//    window.print() sehingga user bisa Save as PDF dari dialog print browser.

import { formatDate, displayValue } from '../../../utils/format';

const esc = s =>
  String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

const delegateValue = value =>
  value === true || value === 1 || String(value).toLowerCase() === 'true';

// Isi kolom "Ket.": delegate -> 'Delegate'; selain itu -> attendanceType
// (field `keterangan` hasil normalisasi), '-' bila kosong.
const ketLabelHtml = item =>
  delegateValue(item?.isDelegate)
    ? 'Delegate'
    : esc(displayValue(item?.keterangan, '-'));

// === Pengaturan lebar kolom tabel attendance (total harus 100%) ===
// Cukup ubah nilai di sini untuk mengatur lebar kolom hasil print/PDF.
const ATT_COLUMN_WIDTHS = {
  no: '5%',
  namaKehadiran: '20%',
  fungsiJabatan: '15%',
  email: '22%',
  status: '14%',
  keterangan: '12%',
  paraf: '12%',
};

const attColgroupHtml = () => `<colgroup>
  <col style="width:${ATT_COLUMN_WIDTHS.no}" />
  <col style="width:${ATT_COLUMN_WIDTHS.namaKehadiran}" />
  <col style="width:${ATT_COLUMN_WIDTHS.fungsiJabatan}" />
  <col style="width:${ATT_COLUMN_WIDTHS.email}" />
  <col style="width:${ATT_COLUMN_WIDTHS.status}" />
  <col style="width:${ATT_COLUMN_WIDTHS.keterangan}" />
  <col style="width:${ATT_COLUMN_WIDTHS.paraf}" />
</colgroup>`;

const attendanceRowsHtml = items =>
  (items || [])
    .map(
      (item, index) => `<tr>
        <td class="center">${index + 1}.</td>
        <td>${esc(displayValue(item?.namaKehadiran, '-'))}</td>
        <td>${esc(displayValue(item?.jabatan, '-'))}</td>
        <td>${esc(displayValue(item?.email, '-'))}</td>
        <td>${esc(displayValue(item?.status, '-'))}</td>
        <td>${ketLabelHtml(item)}</td>
        <td></td>
      </tr>`
    )
    .join('');

export const buildAttendanceHtml = ({ procurement, items }) => {
  const startTime = displayValue(procurement?.invitationStartTime, '-');
  const waktu = startTime === '-' ? '-' : `${startTime} - Selesai`;

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<title>Daftar_Hadir_Evaluasi_Tender_${esc(
    procurement?.procurementNumber || ''
  )}</title>
<style>
  body { font-family: Calibri, Arial, Helvetica, sans-serif; font-size: 12px; color: #222; margin: 0; background: #9a9a9a; }
  .sheet { width: 210mm; min-height: 297mm; margin: 24px auto; padding: 15mm 18mm; background: #fff; box-shadow: 0 2px 12px rgba(0,0,0,.45); box-sizing: border-box; }
  h1 { font-size: 16px; text-align: center; margin: 0 0 18px; font-weight: bold; }
  table.meta { border-collapse: collapse; margin-bottom: 18px; }
  table.meta td { padding: 2px 8px 2px 0; vertical-align: top; }
  table.meta td.label { font-weight: bold; white-space: nowrap; }
  table.att { border-collapse: collapse; width: 100%; table-layout: fixed; border: 0.5px solid #555; }


  table.att th, table.att td { border: 0.35mm solid #555; padding: 6px 8px; vertical-align: top; word-wrap: break-word; overflow-wrap: anywhere; }
  table.att th { text-align: center; background: #f2f2f2; }
  .center { text-align: center; }
  @media print {
    @page { size: A4; margin: 15mm; }
    body { margin: 0; background: #fff; }
    .sheet { width: auto; min-height: 0; margin: 0; padding: 0; background: #fff; box-shadow: none; }
  }
</style>
</head>
<body>
<div class="sheet">
  <h1>DAFTAR HADIR EVALUASI TENDER PROCUREMENT PRODUCT</h1>

  <table class="meta">
    <tr>
      <td class="label">Perihal</td>
      <td>: ${esc(displayValue(procurement?.invitationAgenda, '-'))}</td>
    </tr>
    <tr>
      <td class="label">Tanggal</td>
      <td>: ${esc(formatDate(procurement?.invitationEventDate))}</td>
    </tr>
    <tr>
      <td class="label">Waktu</td>
      <td>: ${esc(waktu)}</td>
    </tr>
    <tr>
      <td class="label">Tempat</td>
      <td>: ${esc(displayValue(procurement?.invitationLocation, '-'))}</td>
    </tr>
  </table>

  <table class="att">
    ${attColgroupHtml()}
    <thead>
      <tr>
        <th>No</th>
        <th>Nama Kehadiran</th>
        <th>Fungsi/Jabatan</th>
        <th>Email</th>
        <th>Status</th>
        <th>Ket.</th>
        <th>Paraf</th>
      </tr>
    </thead>
    <tbody>${attendanceRowsHtml(items)}</tbody>
  </table>
</div>
</body>
</html>`;
};

const openDocWindow = data => {
  const w = window.open('', '_blank');
  if (!w) return null; // popup diblokir browser
  w.document.write(buildAttendanceHtml(data));
  w.document.close();
  return w;
};

export const printAttendancePdf = data => {
  const w = openDocWindow(data);
  if (!w) return;
  // Tanpa aset eksternal, dokumen siap segera — beri jeda kecil supaya layout
  // selesai sebelum dialog print (user memilih "Save as PDF" di sana).
  w.focus();
  setTimeout(() => w.print(), 400);
};
